/**
 * `PlayerView`: what one seat or spectator sees (engine `viewFor`, docs/design/05 §5). The server sends a
 * full view with every update; the client renders it and never derives game state on its own.
 */
import { z } from 'zod';
import { CostSpecSchema, DecisionSchema, PaymentChoiceSchema, PromptSchema } from './decisions';
import {
  AudienceSchema,
  BattlefieldIdSchema,
  CardIdSchema,
  CardRefSchema,
  ChainItemIdSchema,
  DecisionKindSchema,
  DomainSchema,
  JsonObjectSchema,
  ObjectCategorySchema,
  ObjectIdSchema,
  PhaseSchema,
  PlayerIdSchema,
  TierSchema,
  ViewerSchema,
  ZoneRefSchema,
  int,
  text,
} from './primitives';

const count = int(0, 10_000);
const seq = int(0, Number.MAX_SAFE_INTEGER);

export const ModeConfigSchema = z.strictObject({
  id: z.literal('duel'),
  players: int(1, 8),
  teams: z.array(z.array(int(0, 7))).nullable(),
  victoryScore: int(1, 99),
  battlefieldsPerPlayer: int(0, 9),
  battlefieldsInPlayPerPlayer: int(0, 9),
  battlefieldSelection: z.enum(['random', 'choose']),
  firstPlayerBattlefieldsUnused: z.boolean(),
  openingHand: int(0, 20),
  mulliganMax: int(0, 20),
  channelPerTurn: int(0, 20),
  drawPerTurn: int(0, 20),
  firstTurn: z.strictObject({ firstPlayerSkipsDraw: z.boolean(), lastPlayerExtraChannel: int(0, 20) }),
  bestOf: int(1, 9),
});

export const PoolUnitSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('energy'), restriction: z.string().max(64).nullable() }),
  z.strictObject({
    kind: z.literal('power'),
    domain: z.union([DomainSchema, z.literal('any')]),
    restriction: z.string().max(64).nullable(),
  }),
]);

export const SeatSettingsSchema = z.strictObject({
  autoReady: z.boolean(),
  autoChannel: z.boolean(),
  autoDraw: z.boolean(),
  autoHeal: z.boolean(),
  autoPaySuggestion: z.boolean(),
  triggerReminders: z.boolean(),
  warnings: z.boolean(),
  holdPriority: z.boolean(),
});

export const TurnStateSchema = z.strictObject({
  number: count,
  activePlayer: PlayerIdSchema,
  phase: PhaseSchema,
  step: z.string().max(64).nullable(),
  order: z.array(PlayerIdSchema),
  queue: z.array(PlayerIdSchema),
  firstTurn: z.record(PlayerIdSchema, z.boolean()),
});

export const StatusViewSchema = z.strictObject({
  kind: text(32),
  source: ObjectIdSchema.nullable(),
  until: z.enum(['endOfTurn', 'endOfCombat']).nullable(),
});

export const ObjectViewSchema = z.strictObject({
  id: ObjectIdSchema,
  category: ObjectCategorySchema,
  isToken: z.boolean(),
  card: CardRefSchema.nullable(),
  owner: PlayerIdSchema,
  controller: PlayerIdSchema,
  zone: ZoneRefSchema,
  exhausted: z.boolean(),
  damage: count,
  buffs: count,
  counters: z.record(z.string(), count),
  statuses: z.array(StatusViewSchema),
  attachedTo: ObjectIdSchema.nullable(),
  faceDown: z.boolean(),
  revealedTo: AudienceSchema,
});

export const DeckViewSchema = z.strictObject({
  count,
  known: z.array(z.strictObject({ index: count, id: ObjectIdSchema })),
});

export const ZonesViewSchema = z.strictObject({
  hand: z.strictObject({ count, visible: z.array(ObjectIdSchema) }),
  mainDeck: DeckViewSchema,
  runeDeck: DeckViewSchema,
  trash: z.array(ObjectIdSchema),
  banishment: z.array(ObjectIdSchema),
  champion: z.array(ObjectIdSchema),
  legend: z.array(ObjectIdSchema),
  base: z.array(ObjectIdSchema),
  runes: z.array(ObjectIdSchema),
});

export const SeatViewSchema = z.strictObject({
  id: PlayerIdSchema,
  seat: int(0, 7),
  team: int(0, 7).nullable(),
  status: z.enum(['playing', 'conceded', 'eliminated']),
  points: count,
  xp: count,
  pool: z.array(PoolUnitSchema),
  firstTurnAdjustment: z.strictObject({ extraChannel: int(0, 20), skipDraw: z.boolean() }),
  settings: SeatSettingsSchema,
});

export const BattlefieldStateSchema = z.strictObject({
  id: BattlefieldIdSchema,
  card: ObjectIdSchema,
  controller: PlayerIdSchema.nullable(),
  contestedBy: PlayerIdSchema.nullable(),
  permanents: z.array(ObjectIdSchema),
  facedown: z.array(ObjectIdSchema),
  facedownCapacity: int(0, 99),
  scoredThisTurn: z.array(PlayerIdSchema),
  showdownStaged: z.boolean(),
  combatStaged: z.boolean(),
});

export const ChainItemRulesSchema = z.strictObject({
  ability: int(0, 99).nullable(),
  mode: z.array(int(0, 99)),
  targets: z.array(z.strictObject({ instruction: int(0, 999), objects: z.array(ObjectIdSchema) })),
  paid: JsonObjectSchema,
  xValue: int(0, 999).nullable(),
  openedChain: z.boolean(),
  openedBy: z.enum(['card', 'activated', 'triggered', 'add']).nullable(),
  triggerData: JsonObjectSchema.nullable(),
  sourceSnapshot: JsonObjectSchema.nullable(),
});

export const ChainItemKindSchema = z.enum(['card', 'token', 'activated', 'triggered', 'reflexive', 'delayedTrigger']);

export const ChainItemViewSchema = z.strictObject({
  id: ChainItemIdSchema,
  kind: ChainItemKindSchema,
  object: ObjectIdSchema.nullable(),
  source: ObjectIdSchema.nullable(),
  sourceCard: CardRefSchema,
  controller: PlayerIdSchema,
  state: z.enum(['pending', 'finalized']),
  manual: z.boolean(),
  rules: ChainItemRulesSchema.nullable(),
});

export const ShowdownStateSchema = z.strictObject({
  battlefield: BattlefieldIdSchema,
  kind: z.enum(['nonCombat', 'combat']),
  focus: PlayerIdSchema,
  passes: count,
  attacker: PlayerIdSchema.nullable(),
  defender: PlayerIdSchema.nullable(),
  step: z.enum(['showdown', 'damage', 'resolution']),
});

export const PendingDecisionViewSchema = z.strictObject({
  player: PlayerIdSchema,
  kind: DecisionKindSchema,
  decision: DecisionSchema.nullable(),
});

export const SetupViewSchema = z.strictObject({
  step: z.enum(['battlefields', 'mulligan']),
  pending: z.array(PlayerIdSchema),
  mulligans: z.record(PlayerIdSchema, int(0, 10).nullable()),
  battlefieldChosen: z.record(PlayerIdSchema, z.boolean()),
  ownBattlefieldOptions: z.array(CardRefSchema).nullable(),
  ownBattlefieldPick: int(0, 9).nullable(),
});

export const UndoRequestSchema = z.strictObject({
  by: PlayerIdSchema,
  count: int(1, 99),
  seq,
  awaiting: z.array(PlayerIdSchema),
});

export const UndoViewSchema = z.strictObject({
  request: UndoRequestSchema.nullable(),
  undoable: int(0, 99),
  steps: z.array(z.strictObject({ seq, by: PlayerIdSchema, command: text(32) })),
});

export const GameEndReasonSchema = z.enum(['points', 'concede', 'timeout', 'abandon', 'draw', 'noResult', 'aborted']);

export const GameResultSchema = z.strictObject({
  winners: z.array(PlayerIdSchema),
  reason: GameEndReasonSchema,
  seq,
  turn: count,
  points: z.record(PlayerIdSchema, count),
});

/** Tier-2 advice (draft contract owned by the rules layer; `null` in tier 1). */
const AdvisedActionSchema = z.strictObject({
  id: text(64),
  kind: z.enum(['play', 'activate', 'move', 'hide']),
  object: ObjectIdSchema,
  reasons: z.array(text(32)),
  destinations: z.array(text(64)),
  targets: z.array(ObjectIdSchema),
  cost: CostSpecSchema.nullable(),
  payable: z.boolean(),
  payment: z.array(PaymentChoiceSchema).nullable(),
});

const CombatAdviceSchema = z.strictObject({
  battlefield: BattlefieldIdSchema,
  attacker: PlayerIdSchema,
  defender: PlayerIdSchema,
  started: z.boolean(),
  attackerMight: count,
  defenderMight: count,
  assignments: z.array(z.strictObject({ by: PlayerIdSchema, target: ObjectIdSchema, amount: count })),
  deaths: z.array(ObjectIdSchema),
  result: z.enum(['attacker', 'defender', 'none']),
});

const ScoreAdviceSchema = z.strictObject({
  battlefield: BattlefieldIdSchema,
  method: z.enum(['conquer', 'hold']),
  gains: z.enum(['point', 'draw', 'nothing']),
});

const ObjectAdviceSchema = z.strictObject({
  might: int(-1000, 1000).nullable(),
  keywords: z.array(text(64)),
  energy: int(0, 99).nullable(),
  power: int(0, 99).nullable(),
  status: z.enum(['implemented', 'partial', 'manual']),
});

export const WarningCodeSchema = z.enum([
  'timing',
  'cost',
  'location',
  'target',
  'exhausted',
  'destination',
  'prohibited',
]);

export const AdviceViewSchema = z.strictObject({
  playable: z.array(ObjectIdSchema),
  reminders: z.array(
    z.strictObject({
      id: text(64),
      source: ObjectIdSchema,
      kind: z.enum(['trigger', 'bookkeeping']),
      prompt: PromptSchema,
    }),
  ),
  recommendations: z.array(
    z.strictObject({
      id: text(64),
      source: ObjectIdSchema.nullable(),
      prompt: PromptSchema,
      needs: z.array(DecisionKindSchema),
    }),
  ),
  manual: z.array(CardIdSchema),
  warnings: z.array(
    z.strictObject({
      id: text(64),
      code: WarningCodeSchema.exactOptional(),
      prompt: PromptSchema,
      objects: z.array(ObjectIdSchema),
    }),
  ),
  // Rules knowledge layer additions (additive, protocol 1).
  actions: z.array(AdvisedActionSchema).exactOptional(),
  partial: z.array(CardIdSchema).exactOptional(),
  combat: z.array(CombatAdviceSchema).exactOptional(),
  scoring: z.array(ScoreAdviceSchema).exactOptional(),
  objects: z.record(ObjectIdSchema, ObjectAdviceSchema).exactOptional(),
});

export const PlayerViewSchema = z.strictObject({
  schema: int(1, 9999),
  matchId: text(128),
  viewer: ViewerSchema,
  seq,
  tier: TierSchema,
  mode: ModeConfigSchema,
  versions: z.strictObject({ engine: text(64), rules: text(64), catalog: text(64) }),
  commitment: z.string().regex(/^[0-9a-f]{64}$/),
  status: z.enum(['setup', 'playing', 'ended']),
  setup: SetupViewSchema.nullable(),
  turn: TurnStateSchema,
  priority: PlayerIdSchema.nullable(),
  players: z.record(PlayerIdSchema, SeatViewSchema),
  objects: z.record(ObjectIdSchema, ObjectViewSchema),
  zones: z.record(PlayerIdSchema, ZonesViewSchema),
  battlefields: z.array(BattlefieldStateSchema),
  chain: z.array(ChainItemViewSchema),
  showdown: ShowdownStateSchema.nullable(),
  decision: PendingDecisionViewSchema.nullable(),
  undo: UndoViewSchema,
  result: GameResultSchema.nullable(),
  advice: AdviceViewSchema.nullable(),
});
